import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { get } from 'lodash'
import moment from 'moment'
import { FormattedMessage } from 'react-intl'
import { compose } from 'recompose'
import queryString from 'query-string'
import { RadioGroup, Radio } from 'components/fields'
import { wizard } from 'helpers/decorators'
import { nextLevel } from 'constants/wizards'
import {
  apiAwards,
  apiNextLevel,
  apiConfiguration,
  apiParticipants
} from 'helpers/decorators/api'
import { Title, Note, Button, FormDebug } from 'components/misc'
import validate from './level.validation'
import { nextLevelInitialize } from 'models'
import { AGE, CONFIGURATION_KEYS, LEVELS } from 'constants/enums'
import config from 'config'
import { allowedPaymentKeys, skippablePaymentTypes } from 'constants/wizards/participant'
import { getPaymentTypeId } from 'helpers/getPaymentTypeId'

const enhance = compose(
  apiConfiguration(),
  apiNextLevel(),
  apiParticipants(),
  apiAwards(),
  wizard({
    form: 'nextLevelRegistration',
    multipage: true,
    destroyOnUnmount: false,
    validate,
    disableHook: true
  })
)

class Level extends Component {
  static propTypes = {
    initialized: PropTypes.bool.isRequired,
    getRegistrationInfo: PropTypes.func.isRequired,
    onGetAllowedLevels: PropTypes.func.isRequired,
    notification: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    changeWizardActive: PropTypes.func.isRequired,
    changeWizardNext: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onInitialize: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
    location: PropTypes.object,
    awards: PropTypes.object,
    errors: PropTypes.object,
    getConfigurationKeyValue: PropTypes.func,
    getRegistrationOptions: PropTypes.func,
    change: PropTypes.func,
    handleGetRegistrationFieldsCustomization: PropTypes.func.isRequired
  }

  componentDidMount = async() => {
    const {
      values,
      onGetAllowedLevels
    } = this.props

    const query = queryString.parse(get(this.props, 'location.search'))
    const authorizationCode = get(query, 'authorizationCode')
    const registration = get(this.props, 'nextLevel.awardInfo')

    if (registration) {
      try {
        const orgId = get(values, 'organizationId')
        const birthDate = get(registration, 'birthDate')
        const { steps, rootPath } = nextLevel

        await this.handleLoadFieldCustomizations(orgId)


        if (moment().diff(birthDate, 'years') < AGE.adult) {
          this.props.changeWizardActive(steps.parent.id, true)
          this.props.changeWizardNext(steps.details.id, rootPath + steps.parent.path)
        } else {
          this.props.changeWizardActive(steps.parent.id, false)
          this.props.changeWizardNext(steps.details.id, rootPath + steps.profile.path)
        }

        let initData = nextLevelInitialize(registration)

        const nextLevelOrganizationChanged = get(this.props, 'values.helpers.nextLevelOrganizationChanged')

        initData = {
          ...initData,
            nextLevelOrganizationChanged,
            organizationId: orgId,
            awardLeaderId: get(values, 'awardLeaderId')
        }

        this.props.initialize(initData)
        this.props.onInitialize()
        this.props.change('helpers.registrationId', get(registration, 'id'))

        return onGetAllowedLevels(authorizationCode)
      } catch (err) {
        const errorCode = get(err, 'body') && JSON.parse(err.body).errorCode

        if (errorCode === 'UNAUTHORIZED') {
          this.props.notification(
            'Authorisation code is not valid - it looks like you have previously used this code',
            {
              intlKey: 'notifications.authorization_code_is_not_valid',
              status: 'error'
            }
          )
          this.props.push(`${config.publicPath}/`)
        }
      }
    }
  }

  handleLoadFieldCustomizations = async orgId => {
    await this.props.handleGetRegistrationFieldsCustomization(orgId)
  }

  handleSubmit = async fields => {
    const { steps } = nextLevel
    const { body } = await this.props.getRegistrationOptions({
      isDirectLevelRegistration: false,
      organizationId: get(fields, 'organizationId'),
      awardLevel: get(fields, 'awardLevel.value'),
      registrationId: get(fields, 'helpers.registrationId', undefined)
    })

    const paymentOpts = get(body, 'paymentOptions', {})
    const baseAmount = paymentOpts.baseAmount
    const paymentTypes = allowedPaymentKeys.filter(
      key => paymentOpts[key] === true
    )

    // Intentionally avoid simplifying this condition.
    // We explicitly want to *only* disable the payment step when baseAmount is exactly 0.
    if (
      paymentTypes.some(item => skippablePaymentTypes.includes(getPaymentTypeId(item)))
      && parseFloat(baseAmount) === 0
    ) {
      this.props.changeWizardActive(steps.payment.id, false)
    }

    this.props.change(
      'helpers.awardLevelConfigurations',
      get(body, 'awardLevelConfigurations', {})
    )

    this.props.onSubmit(fields)
  }

  handleChangeAwardLevel = value => {
    const orgId = get(this.props.values, 'organizationId')

    this.handleSkipPayment(value, orgId)
  }

  handleSkipPayment = async(awardLvl, orgId) => {
    const { steps } = nextLevel

    let res

    switch (awardLvl) {
      case LEVELS.BRONZE.id:
        res = orgId && await this.props.getConfigurationKeyValue(CONFIGURATION_KEYS.participantRegistrationSkipPayment.id, orgId)
        break
      case LEVELS.SILVER.id:
        res = orgId && await this.props.getConfigurationKeyValue(CONFIGURATION_KEYS.participantRegistrationSkipPaymentSilver.id, orgId)
        break
      case LEVELS.GOLD.id:
        res = orgId && await this.props.getConfigurationKeyValue(CONFIGURATION_KEYS.participantRegistrationSkipPaymentGold.id, orgId)
        break
      default:
        res = orgId && await this.props.getConfigurationKeyValue(CONFIGURATION_KEYS.participantRegistrationSkipPayment.id, orgId)
    }

    const skipPayment = get(res, 'body.booleanValue') || false

    this.props.changeWizardActive(steps.payment.id, !skipPayment)
  }

  render() {
    const { handleSubmit, awards } = this.props

    return (
      <form
        onSubmit={handleSubmit(this.handleSubmit)}
      >
        <Title h1>
          <FormattedMessage
            id="moduleTitle.registration_for_level"
            defaultMessage="Registration for new level"
          />
        </Title>

        <div className="row">
          <div className="col-xs-6">
            {awards.allowedLevels &&
              awards.allowedLevels[0] &&
              <RadioGroup
                name="awardLevel.value"
                label="What level of the Award are you registering for?"
                intlKey="label.what_level_of_the_award"
                errors={this.props.errors}
                horizontal
              >
                {awards.allowedLevels.map(item => (
                  <Radio
                    label={item.label}
                    name="awardLevel.value"
                    value={item.value}
                    key={item.value}
                    onChange={value => this.handleChangeAwardLevel(value)}
                  />
                ))}
              </RadioGroup>}
          </div>
        </div>

        <Note lighter margin="0 0 35px 0">
          <FormattedMessage
            id="label.before_entering_new_level"
            defaultMessage="Before entering the new level please confirm that your personal details are still up to date"
          />
        </Note>

        <FormDebug {...this.props} />
        <Button>
          <FormattedMessage id="button.continue" defaultMessage="Continue" />
        </Button>
      </form>
    )
  }
}

export default enhance(Level)
